import { FullScreenProps } from '@/components/FullScreen';
import { Table, TableRef } from '@/components/Table';
import { WithLoader } from '@/components/WithLoader';
import { useConfigContext } from '@/context/configContext';
import { SuggestedBudgetCellRender } from '@/components/SuggestedBudgetCell/SuggestedBudgetCellRender';
import { MediaInvestmentCellRender } from '@/dashboards/CampaignOptimization/components/MediaInvestmentTable/MediaInvestmentCellRender';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { roundToDecimals } from '@/utils/numberUtils';
import { Typography, Button } from '@analytical-alley/ui';
import { IconDownload } from '@tabler/icons-react';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';

export type MediaInvestmentTableData = {
    category: string;
    variable: string;
    previousPeriodInvestments: number;
    recommendedBudget: number;
    contribution: number;
};

const columnHelper = createColumnHelper<MediaInvestmentTableData>();

export const MediaInvestmentTableBase = ({
    fullScreenButton,
}: FullScreenProps) => {
    const {
        data: {
            suggestedBudgetData: rawSuggestedBudgetData,
            simulatedInvestmentTotal,
        },
        disabledChannels,
        isDataLoading,
        isDataFetching,
    } = useOptimizationSession();
    const { formatNumber } = useConfigContext();
    const { contributionFormatter, variableNameMap } =
        useDashboardGlobalContext();

    const disabledChannelsNames = useMemo(() => {
        return disabledChannels.map((channel) => variableNameMap[channel]);
    }, [disabledChannels, variableNameMap]);

    const suggestedBudgetData = rawSuggestedBudgetData.filter(
        (entry) => !disabledChannelsNames.includes(entry.variable),
    );

    const recommendedBudgetShare = useCallback(
        (row: { recommendedBudget: number }) => {
            return roundToDecimals(
                (row.recommendedBudget / simulatedInvestmentTotal) * 100,
                2,
            );
        },
        [simulatedInvestmentTotal],
    );

    const columns = useMemo(
        () => [
            columnHelper.accessor('category', {
                header: () => 'Category',
                cell: (info) => {
                    return <MediaInvestmentCellRender {...info} disabled />;
                },
                footer: () => 'Total',
                meta: {
                    header: {
                        className: 'w-px',
                    },
                    footer: {
                        className: 'text-right',
                    },
                },
            }),
            columnHelper.accessor('variable', {
                cell: (info) => {
                    return <MediaInvestmentCellRender {...info} disabled />;
                },
                header: () => <span>Variable</span>,
            }),
            columnHelper.accessor('recommendedBudget', {
                header: () => (
                    <Typography tooltip="How much we recommend investing in this channel given the restrictions.">
                        Recommended budget
                    </Typography>
                ),
                cell: (info) => {
                    const recommendedBudget = info.getValue();

                    return (
                        <MediaInvestmentCellRender
                            {...info}
                            className="justify-center"
                            value={formatNumber(recommendedBudget)}
                            valueTitle="Recommended budget"
                        >
                            <div className="flex">
                                {formatNumber(recommendedBudget)}
                            </div>
                        </MediaInvestmentCellRender>
                    );
                },
                meta: {
                    header: {
                        className: 'w-px text-center',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                footer: (info) => {
                    return (
                        <span>
                            {formatNumber(
                                info.table
                                    .getRowModel()
                                    .rows.reduce(
                                        (acc, row) =>
                                            acc +
                                            row.original.recommendedBudget,
                                        0,
                                    ),
                            )}
                        </span>
                    );
                },
            }),
            columnHelper.accessor(recommendedBudgetShare, {
                id: 'recommendedBudgetShare',
                header: 'Recommended budget share',

                cell: (info) => {
                    const percent = info.getValue();

                    return (
                        <SuggestedBudgetCellRender
                            {...info}
                            className="justify-center"
                            value={`${percent} %`}
                        >
                            <div className="flex">{percent}%</div>
                        </SuggestedBudgetCellRender>
                    );
                },
                meta: {
                    header: {
                        className: 'w-px text-center',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                footer: () => {
                    return <span>100 %</span>;
                },
            }),
            columnHelper.accessor('contribution', {
                header: () => (
                    <Typography tooltip="How much this channel is predicted to generate over the campaign period.">
                        Contribution
                    </Typography>
                ),
                meta: {
                    header: {
                        className: 'w-px text-center',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                cell: (info) => {
                    return (
                        <MediaInvestmentCellRender
                            {...info}
                            className="justify-center"
                            value={contributionFormatter(info.getValue())}
                            valueTitle="Contribution"
                        >
                            {contributionFormatter(info.getValue())}
                        </MediaInvestmentCellRender>
                    );
                },
                footer: (info) => {
                    const totalContribution = info.table
                        .getRowModel()
                        .rows.reduce(
                            (acc, row) => acc + row.original.contribution,
                            0,
                        );
                    return (
                        <span className="text-center">
                            {contributionFormatter(totalContribution)}
                        </span>
                    );
                },
            }),
        ],
        [recommendedBudgetShare, formatNumber, contributionFormatter],
    );

    const tableRef = React.useRef<TableRef>(null);

    const downloadXls = useCallback(() => {
        // Calculate totals for export
        const getTotalsRow = () => {
            if (!suggestedBudgetData?.length) return undefined;

            const totalPreviousPeriodInvestments = suggestedBudgetData.reduce(
                (acc, row) => acc + row.previousPeriodInvestments,
                0,
            );

            const totalRecommendedBudget = suggestedBudgetData.reduce(
                (acc, row) => acc + row.recommendedBudget,
                0,
            );

            const totalContribution = suggestedBudgetData.reduce(
                (acc, row) => acc + row.contribution,
                0,
            );

            return {
                category: 'Total',
                variable: '',
                previousPeriodInvestments: formatNumber(
                    totalPreviousPeriodInvestments,
                ),
                recommendedBudget: formatNumber(totalRecommendedBudget),
                recommendedBudgetShare: '100 %',
                contribution: contributionFormatter(totalContribution),
            };
        };

        tableRef.current?.downloadXls(
            'media-investment-split',
            { addDate: true },
            getTotalsRow(),
        );
    }, [suggestedBudgetData, formatNumber, contributionFormatter]);

    return (
        <div className="relative">
            <WithLoader isLoading={isDataLoading} isFetching={isDataFetching}>
                <div className="glass-tile p-6">
                    <div className="flex items-center mb-4">
                        <Typography
                            variant="titleS"
                            tooltip="Your optimisation summary. Compare historic spending against the recommended values and the predicted KPI contribution."
                        >
                            Media Investment Split
                        </Typography>
                        <span className="grow" />
                        <div className="flex gap-2">
                            {fullScreenButton}
                            <Button
                                className="glass-tile w-8 h-8 flex justify-center items-center p-2 rounded-md shadow-lg hover:bg-white/30"
                                onClick={downloadXls}
                                tooltip="Download the optimisation summary."
                            >
                                <IconDownload />
                            </Button>
                        </div>
                    </div>

                    <Table
                        tableRef={tableRef}
                        className="overflow-x-auto"
                        data={suggestedBudgetData}
                        columns={columns}
                        onRowExport={({
                            recommendedBudget,
                            contribution,
                            ...restOfRow
                        }) => {
                            return {
                                ...restOfRow,
                                recommendedBudget: formatNumber(recommendedBudget),
                                recommendedBudgetShare: `${recommendedBudgetShare({
                                    recommendedBudget,
                                })} %`,
                                contribution: contributionFormatter(contribution),
                            };
                        }}
                        shouldSpanRow
                        colSpan={{
                            footer: {
                                value: 2,
                                index: 0,
                            },
                        }}
                    />
                </div>
            </WithLoader>
        </div>
    );
};
